class PlayShowroomHeader extends HTMLElement {
  connectedCallback() {
    this.listeners?.abort();
    this.listeners = new AbortController();
    const options = { signal: this.listeners.signal };
    this.menu = this.querySelector('[data-ps-menu]');
    this.toggle = this.querySelector('[data-ps-menu-toggle]');
    this.mobileViewport = window.matchMedia('(max-width: 1180px)');

    this.toggle?.addEventListener('click', () => {
      this.setMenu(!this.classList.contains('is-open'), true);
    }, options);
    this.querySelectorAll('[data-ps-menu-close]').forEach((button) => {
      button.addEventListener('click', () => this.setMenu(false, true), options);
    });
    this.addEventListener('keydown', (event) => this.handleMenuKeydown(event), options);
    this.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', () => this.setMenu(false), options);
    });
    this.menu?.addEventListener('submit', () => this.setMenu(false), options);
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) this.setMenu(false);
    }, options);

    const updateViewport = () => {
      if (!this.classList.contains('is-open')) return;
      if (!this.mobileViewport.matches) {
        const focusWasInMenu = this.menu?.contains(document.activeElement);
        this.setMenu(false);
        if (focusWasInMenu) this.querySelector('.ps-wordmark')?.focus({ preventScroll: true });
      } else {
        this.updateMenuHeight();
      }
    };
    window.addEventListener('resize', updateViewport, options);
    window.visualViewport?.addEventListener('resize', updateViewport, options);
    this.setMenu(false);
  }

  disconnectedCallback() {
    this.listeners?.abort();
    document.documentElement.classList.remove('ps-menu-open');
    this.unlockScroll();
  }

  lockScroll() {
    if (this.scrollState) return;
    const style = document.body.style;
    this.scrollState = {
      x: window.scrollX,
      y: window.scrollY,
      styles: ['position', 'top', 'left', 'right', 'width'].map((name) => [
        name, style.getPropertyValue(name), style.getPropertyPriority(name)
      ])
    };
    style.position = 'fixed';
    style.top = `${-this.scrollState.y}px`;
    style.left = '0';
    style.right = '0';
    style.width = '100%';
  }

  unlockScroll() {
    if (!this.scrollState) return;
    const { x, y, styles } = this.scrollState;
    this.scrollState = null;
    styles.forEach(([name, value, priority]) => {
      if (value) document.body.style.setProperty(name, value, priority);
      else document.body.style.removeProperty(name);
    });
    const rootStyle = document.documentElement.style;
    const behavior = rootStyle.scrollBehavior;
    rootStyle.scrollBehavior = 'auto';
    window.scrollTo(x, y);
    rootStyle.scrollBehavior = behavior;
  }

  updateMenuHeight() {
    const viewport = window.visualViewport;
    const bottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
    const border = parseFloat(getComputedStyle(this).borderBottomWidth) || 0;
    const top = this.getBoundingClientRect().bottom - border;
    this.style.setProperty('--ps-mobile-menu-height', `${Math.max(0, bottom - top)}px`);
  }

  setMenu(open, restoreFocus = false) {
    open = Boolean(open && this.mobileViewport.matches);
    this.classList.toggle('is-open', open);
    this.toggle?.setAttribute('aria-expanded', String(open));
    this.toggle?.setAttribute('aria-label', open ? 'Close navigation' : 'Open navigation');
    this.menu?.setAttribute('aria-hidden', String(!open));
    if (this.menu) this.menu.inert = !open;
    document.documentElement.classList.toggle('ps-menu-open', open);
    if (open) {
      this.lockScroll();
      this.updateMenuHeight();
      const nav = this.menu?.querySelector('nav');
      if (nav) nav.scrollTop = 0;
    } else {
      this.unlockScroll();
    }
    // Keep the close control reachable without opening the phone's search keyboard.
    if (restoreFocus) this.toggle?.focus({ preventScroll: true });
  }

  handleMenuKeydown(event) {
    if (!this.classList.contains('is-open')) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.setMenu(false, true);
      return;
    }
    if (event.key !== 'Tab') return;
    const selector = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), summary, [tabindex="0"]';
    const controls = [...this.querySelectorAll(selector)].filter((element) => {
      const closedDetails = element.closest('details:not([open])');
      return element.getClientRects().length > 0
        && getComputedStyle(element).visibility !== 'hidden'
        && !element.closest('[inert]')
        && (!closedDetails || element === closedDetails.querySelector('summary'));
    });
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last?.focus({ preventScroll: false });
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus({ preventScroll: true });
    }
  }
}

if (!customElements.get('play-showroom-header')) {
  customElements.define('play-showroom-header', PlayShowroomHeader);
}

document.addEventListener('click', (event) => {
  const anchor = event.target.closest('a[href^="#"]');
  if (!anchor) return;
  const target = document.querySelector(anchor.getAttribute('href'));
  if (!target) return;
  event.preventDefault();
  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
});
